/**
 * GPS-based lean angle calculation
 *
 * Calculates lean angle from GPS trajectory using turn radius and speed.
 * This is a fallback when gyroscope/accelerometer data is not available.
 *
 * Uses sophisticated multi-point radius averaging and smoothing to reduce
 * GPS noise impact on lean angle calculations.
 */

import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import { LeanAnglePoint } from '../domain/telemetry/processed/LeanAngle'
import {
  calculateSamplingFrequencyHz,
  getFrequencyAdaptiveConfig,
} from '../utils/timeUtils'
import { smoothGpsCoordinates } from '../utils/smoothingUtils'
import { calculateDistance } from '../utils/distanceUtils'
import { KMH_PER_MPS } from '../utils/speedUnits'

/**
 * Calculate turn direction using heading change
 * Returns: positive for left turn, negative for right turn
 */
function calculateTurnDirection(
  p1: GpsPoint,
  p2: GpsPoint,
  p3: GpsPoint
): number {
  const heading1 = Math.atan2(
    p2.latitude - p1.latitude,
    p2.longitude - p1.longitude
  )
  const heading2 = Math.atan2(
    p3.latitude - p2.latitude,
    p3.longitude - p2.longitude
  )

  let headingChange = heading2 - heading1

  // Normalize to [-π, π]
  while (headingChange > Math.PI) headingChange -= 2 * Math.PI
  while (headingChange < -Math.PI) headingChange += 2 * Math.PI

  return headingChange
}

/**
 * Calculate turn radius from three GPS points using circumradius formula
 */
function calculateTurnRadius(
  p1: GpsPoint,
  p2: GpsPoint,
  p3: GpsPoint
): number | null {
  const d1 = calculateDistance(
    p1.latitude,
    p1.longitude,
    p2.latitude,
    p2.longitude
  )
  const d2 = calculateDistance(
    p2.latitude,
    p2.longitude,
    p3.latitude,
    p3.longitude
  )
  const d3 = calculateDistance(
    p1.latitude,
    p1.longitude,
    p3.latitude,
    p3.longitude
  )

  // Points must have minimum separation
  if (d1 < 2 || d2 < 2 || d3 < 2) return null

  // Calculate semi-perimeter and area using Heron's formula
  const s = (d1 + d2 + d3) / 2
  const areaSquared = s * (s - d1) * (s - d2) * (s - d3)

  // Collinear points (straight line)
  if (areaSquared <= 0.1) return null

  const area = Math.sqrt(areaSquared)
  const radius = (d1 * d2 * d3) / (4 * area)

  // Filter physically impossible radii
  if (radius < 5 || radius > 10000) return null

  return radius
}

/**
 * Calculate averaged turn radius using multiple point spans for robustness
 */
function calculateAveragedTurnRadius(
  points: GpsPoint[],
  centerIndex: number,
  lookAhead: number = 3
): number | null {
  const validRadii: number[] = []

  for (let offset = 1; offset <= lookAhead; offset++) {
    const prevIndex = centerIndex - offset
    const nextIndex = centerIndex + offset

    if (prevIndex < 0 || nextIndex >= points.length) continue

    const radius = calculateTurnRadius(
      points[prevIndex],
      points[centerIndex],
      points[nextIndex]
    )

    if (radius !== null) validRadii.push(radius)
  }

  if (validRadii.length === 0) return null

  // Use median for robustness
  validRadii.sort((a, b) => a - b)
  const midIndex = Math.floor(validRadii.length / 2)
  return validRadii.length % 2 === 0
    ? (validRadii[midIndex - 1] + validRadii[midIndex]) / 2
    : validRadii[midIndex]
}

/**
 * Apply adaptive smoothing that scales with GPS frequency
 */
function smoothLeanAnglesAdaptive(
  leanAngles: number[],
  freqScale: number
): number[] {
  if (leanAngles.length < 3) return leanAngles

  // Scale window size to cover ~0.8 seconds of data
  const windowSize = Math.round(8 * freqScale)
  const halfWindow = Math.floor(windowSize / 2)
  const smoothingStrength = 0.4 * freqScale

  const smoothed: number[] = [...leanAngles]

  for (let i = 0; i < leanAngles.length; i++) {
    const windowStart = Math.max(0, i - halfWindow)
    const windowEnd = Math.min(leanAngles.length, i + halfWindow + 1)

    let weightedSum = 0
    let totalWeight = 0

    for (let j = windowStart; j < windowEnd; j++) {
      const distance = Math.abs(j - i)
      const weight = Math.exp(
        -(distance * distance) / (2 * smoothingStrength * smoothingStrength)
      )
      weightedSum += leanAngles[j] * weight
      totalWeight += weight
    }

    if (totalWeight > 0) {
      smoothed[i] = weightedSum / totalWeight
    }
  }

  return smoothed
}

/**
 * Apply adaptive rate limiting to prevent unrealistic lean angle changes
 */
function rateLimitLeanAngles(
  leanAngles: number[],
  maxChangePerStep: number = 10
): number[] {
  if (leanAngles.length === 0) return []

  const rateLimited: number[] = [leanAngles[0]]
  let previousAngle = leanAngles[0]

  for (let i = 1; i < leanAngles.length; i++) {
    const current = leanAngles[i]
    const change = current - previousAngle

    // Adaptive rate limiting based on context
    let maxChange = maxChangePerStep

    // Look ahead and behind to determine if we're in a cornering scenario
    const contextWindow = 3
    const windowStart = Math.max(0, i - contextWindow)
    const windowEnd = Math.min(leanAngles.length, i + contextWindow + 1)
    const contextAngles = leanAngles.slice(windowStart, windowEnd)
    const avgAngle =
      contextAngles.reduce((sum, a) => sum + Math.abs(a), 0) /
      contextAngles.length

    // If we're in a corner, allow faster changes
    if (avgAngle > 20) maxChange = maxChangePerStep * 1.5

    // If increasing towards significant lean, be less restrictive
    if (Math.abs(change) > 0 && Math.abs(current) > 10)
      maxChange = maxChangePerStep * 1.3

    // If decreasing from significant lean, allow faster reduction
    if (change < 0 && previousAngle > 15) maxChange = maxChangePerStep * 1.3

    const limitedChange = Math.max(-maxChange, Math.min(maxChange, change))
    const limitedAngle = previousAngle + limitedChange

    rateLimited.push(limitedAngle)
    previousAngle = limitedAngle
  }

  return rateLimited
}

/**
 * Calculate lean angle from GPS trajectory
 *
 * Uses sophisticated multi-point radius averaging and smoothing to produce
 * accurate lean angles from noisy GPS data.
 */
export function calculateLeanAngleFromGps(
  gpsPoints: GpsPoint[]
): LeanAnglePoint[] {
  if (gpsPoints.length < 10) {
    return gpsPoints.map((point) => ({
      leanAngleDegrees: 0,
      timestamp: point.timestamp,
      distanceMeters: point.distanceMeters,
      elapsedMs: point.elapsedMs,
      speedKmh: point.speedKmh,
      source: 'calculated-gps' as const,
    }))
  }

  // Use centralized frequency calculation and configuration
  const frequencyHz = calculateSamplingFrequencyHz(gpsPoints)
  const frequencyConfig = getFrequencyAdaptiveConfig(frequencyHz)

  // Step 1: Smooth GPS coordinates - scale window to cover ~1.5 seconds
  const gpsSmoothing = Math.round(15 * frequencyConfig.frequencyScale)
  const smoothedPoints = smoothGpsCoordinates(gpsPoints, gpsSmoothing)

  // Step 2: Determine lookAhead for radius calculation
  // Need ~3-4 meter separation between points for accurate radius
  const lookAhead = Math.max(3, Math.ceil(frequencyHz / 3))

  // Step 3: Calculate raw lean angles using multi-point radius averaging
  const rawLeanAngles: number[] = []

  for (let i = 0; i < smoothedPoints.length; i++) {
    const point = smoothedPoints[i]

    // Need enough points on each side for multi-point averaging
    if (i < lookAhead || i >= smoothedPoints.length - lookAhead) {
      rawLeanAngles.push(0)
      continue
    }

    // Skip if speed is too low
    if (point.speedKmh < 10) {
      rawLeanAngles.push(0)
      continue
    }

    // Calculate averaged radius
    const radius = calculateAveragedTurnRadius(smoothedPoints, i, lookAhead)

    if (radius === null) {
      rawLeanAngles.push(0)
      continue
    }

    // Calculate centripetal acceleration: a = v² / r
    const speedMs = point.speedKmh / KMH_PER_MPS
    const centripetalAccel = (speedMs * speedMs) / radius

    // Limit unrealistic values
    if (centripetalAccel > 25) {
      rawLeanAngles.push(0)
      continue
    }

    // Calculate lean angle: θ = atan(a / g)
    const leanAngleRadians = Math.atan(centripetalAccel / 9.81)
    const leanAngleMagnitude = (leanAngleRadians * 180) / Math.PI

    // Determine turn direction
    const headingChange = calculateTurnDirection(
      smoothedPoints[i - 1],
      smoothedPoints[i],
      smoothedPoints[i + 1]
    )

    // Apply sign from heading change
    const leanAngleDegrees =
      headingChange >= 0 ? leanAngleMagnitude : -leanAngleMagnitude

    // Apply threshold and clamp to realistic range
    if (Math.abs(leanAngleDegrees) < 0.3) {
      rawLeanAngles.push(0)
    } else {
      const sign = Math.sign(leanAngleDegrees)
      rawLeanAngles.push(sign * Math.min(Math.abs(leanAngleDegrees), 65))
    }
  }

  // Step 4: Apply adaptive rate limiting
  const maxChangePerStep = 7 / frequencyConfig.frequencyScale
  const rateLimited = rateLimitLeanAngles(rawLeanAngles, maxChangePerStep)

  // Step 5: Apply non-causal smoothing for final result
  const smoothed = smoothLeanAnglesAdaptive(
    rateLimited,
    frequencyConfig.frequencyScale
  )

  // Step 6: Convert to LeanAnglePoint array
  return gpsPoints.map((point, index) => ({
    leanAngleDegrees: Math.round(smoothed[index] * 100) / 100,
    timestamp: point.timestamp,
    distanceMeters: point.distanceMeters,
    elapsedMs: point.elapsedMs,
    speedKmh: point.speedKmh,
    source: 'calculated-gps' as const,
  }))
}
